import { useState, useCallback, useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import {
    injectTourStyles,
    cleanupTourElements,
    createTourPopover,
    positionPopover,
    createTourOverlay,
} from '@/utils/tour-styles'

interface TourStep {
    selector: string
    title: string
    description: string
}

export const useAssetListTour = () => {
    const { t } = useTranslation('asset')
    const [isActive, setIsActive] = useState(false)
    const [currentStep, setCurrentStep] = useState(0)

    const TOUR_STEPS: TourStep[] = useMemo(
        () => [
            {
                selector: '[data-tour="create-asset-btn"]',
                title: t('tour.createAsset.title', 'Tạo tài liệu mới'),
                description: t(
                    'tour.createAsset.description',
                    'Nhấn vào đây để bắt đầu tạo tài liệu mới. Bạn có thể tải lên file và nhập thông tin chi tiết.'
                ),
            },
            {
                selector: '[data-tour="asset-tabs"]',
                title: t('tour.assetTabs.title', 'Quản lý danh sách'),
                description: t(
                    'tour.assetTabs.description',
                    'Chuyển đổi giữa các tab để xem:\n• Danh sách: Tất cả tài liệu đang lưu hành\n• Quản lý tài liệu của tôi: Tài liệu do chính bạn tạo ra\n• Nháp: Tài liệu đang soạn thảo và chưa được gửi đi phê duyệt\n• Tài liệu chờ duyệt: Danh sách các tài liệu đang chờ bạn xem xét và phê duyệt (Dành cho Admin/Approver)'
                ),
            },
        ],
        [t]
    )

    const getVisibleSteps = useCallback(() => {
        return TOUR_STEPS.filter((step) => {
            const element = document.querySelector(step.selector)
            return element !== null
        })
    }, [TOUR_STEPS])

    const endTour = useCallback(() => {
        cleanupTourElements()
        setIsActive(false)
        setCurrentStep(0)
    }, [])

    const showTourStepRef = useRef<
        (stepIndex: number, steps: TourStep[]) => void
    >(() => {})

    const showTourStep = useCallback(
        (stepIndex: number, steps: TourStep[]) => {
            injectTourStyles()
            cleanupTourElements()
            createTourOverlay()

            const step = steps[stepIndex]
            if (!step) return

            const element = document.querySelector(step.selector) as HTMLElement
            if (!element) {
                // Skip to next step if element not found
                if (stepIndex < steps.length - 1) {
                    showTourStepRef.current(stepIndex + 1, steps)
                } else {
                    endTour()
                }
                return
            }

            element.classList.add('tour-highlight-element')
            element.scrollIntoView({ behavior: 'smooth', block: 'center' })

            setTimeout(() => {
                const isLastStep = stepIndex === steps.length - 1
                const popover = createTourPopover({
                    title: step.title,
                    description: step.description,
                    currentStep: stepIndex + 1,
                    totalSteps: steps.length,
                    onPrev:
                        stepIndex > 0
                            ? () =>
                                  showTourStepRef.current(stepIndex - 1, steps)
                            : undefined,
                    onNext: isLastStep
                        ? endTour
                        : () => showTourStepRef.current(stepIndex + 1, steps),
                    onClose: endTour,
                    isLastStep,
                })

                document.body.appendChild(popover)
                positionPopover(popover, element)
            }, 300)
        },
        [endTour]
    )

    useEffect(() => {
        showTourStepRef.current = showTourStep
    }, [showTourStep])

    const startTour = useCallback(() => {
        const visibleSteps = getVisibleSteps()
        if (visibleSteps.length === 0) {
            console.warn('No visible tour elements found')
            return
        }

        setIsActive(true)
        setCurrentStep(0)

        // Scroll to top first
        window.scrollTo({ top: 0, behavior: 'smooth' })

        // Start tour after scroll
        setTimeout(() => {
            showTourStep(0, visibleSteps)
        }, 300)
    }, [getVisibleSteps, showTourStep])

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            cleanupTourElements()
        }
    }, [])

    return {
        isActive,
        currentStep,
        startTour,
        endTour,
        totalSteps: getVisibleSteps().length,
    }
}
